import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import cx from 'classnames'

import { ALMANAC_CORE_WORKSPACE_ID } from 'utils/constants'
import { useWorkspaces } from 'hooks/useWorkspaces'
import { UserService } from 'api/clients/UserService'
import { Button } from 'blaze-ui/Button'
import Shared from '../../Shared.module.scss'
import Styles from './CompanyData.module.scss'
import { useAuth } from 'hooks/UseAuth'
import { RegistrationType } from 'screens/LoginOrSignup/utils'
import { getUrlParams } from 'utils/workspaceUtils'
import { CompanyDataSelect } from '../CompanyDataSelect/CompanyDataSelect'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import useLoginOrSignUp from 'screens/LoginOrSignup/hooks/useLoginOrSignUp'
import { ArrowRight } from 'blaze-ui/icons'
import { blazeDiscoveryOptions, businessDescriptionOptions, signUpOptions, sizeOptions } from './utils'
import ProgressBar from '../ProgressBar/ProgressBar'
import { Form, Formik } from 'formik'
import { useUpdateRegistrationData } from 'hooks/queries/useUpdateRegistrationData'
import { object, string } from 'yup'
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from 'components/Wizards/Wizard/Form/LanguageInputField/LanguageInput'
import { Portal } from 'components/Portal/Portal'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { FormSchema } from 'components/Wizards/Wizard/Form/Types'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { useRegistrationData } from 'hooks/queries/users/useRegistrationData'

type Props = {
  transitionPage: (page: string) => void
  isInvitedUser: boolean
  redirectTo: string
  workspaceSeeded: boolean
  registrationType: RegistrationType
  onboardingDocPath?: string
  totalSteps: number
  onWorkspaceLoaded: (id: number) => void
}

interface Values {
  businessType: string
  signupReason: string
  discoverySource: string
  teamSize: string
  selectedLanguage: string
}

const useUtmParams = () => {
  const urlParams = getUrlParams()
  const searchParams = useMemo(() => new URLSearchParams(urlParams), [urlParams])
  const utm_medium = searchParams.get('utm_medium')
  const utm_campaign = searchParams.get('utm_campaign')
  const utm_content = searchParams.get('utm_content')
  const utm_source = searchParams.get('utm_source')
  const utm_term = searchParams.get('utm_term')
  const referral_page = searchParams.get('referral_page')
  const referrer = searchParams.get('referrer')

  return {
    utm_medium,
    utm_campaign,
    utm_content,
    utm_source,
    utm_term,
    referral_page,
    referrer,
  }
}

const CompanyData = ({
  registrationType,
  onboardingDocPath,
  redirectTo,
  workspaceSeeded,
  transitionPage,
  totalSteps,
  onWorkspaceLoaded,
}: Props) => {
  const workspaces = useWorkspaces()
  const workspace = workspaces.find((w) => w.id !== ALMANAC_CORE_WORKSPACE_ID && w.admin)
  const { goToWorkspace } = useLoginOrSignUp()
  const root = useRef<HTMLDivElement>(null)
  const footerRef = useRef<HTMLDivElement>(null)

  const { isTabletPortrait, isMobile } = useResponsiveDesign()
  const [waitingForSeeds] = useState(false)
  const auth = useAuth()
  const { data } = useRegistrationData()

  const utmParams = useUtmParams()

  useEffect(() => {
    if (!workspace) return
    UserService.setCurrentWorkspaceId(workspace.id)
    onWorkspaceLoaded(workspace.id)
  }, [workspace, onWorkspaceLoaded])

  // Any params here need to be added to the loginAnalyticsParams
  const { mutateAsync: updateRegistrationData } = useUpdateRegistrationData()

  const businessType = string()
    .oneOf(businessDescriptionOptions.map((description) => description.value))
    .required()
  const signupReason = string()
    .oneOf(signUpOptions.map((option) => option.value))
    .required()
  const discoverySource = string()
    .oneOf(blazeDiscoveryOptions.map((discovery) => discovery.value))
    .required()
  const teamSize = string()
    .oneOf(sizeOptions.map((size) => size.value))
    .required()
  const selectedLanguage = string().default(DEFAULT_LANGUAGE).oneOf(SUPPORTED_LANGUAGES).required()

  const validationSchema = object({
    businessType,
    signupReason,
    discoverySource,
    teamSize,
    selectedLanguage,
  }) as FormSchema<GenerationType | ChannelType>

  const redirectToWorkspace = useCallback(
    (
      { withLoader, onboardingFilePath }: { withLoader?: boolean; onboardingFilePath?: string } = { withLoader: true },
    ) => {
      goToWorkspace(workspace, auth, {
        registrationType,
        redirectTo: redirectTo || onboardingFilePath,
        onboardingDocPath,
        recentRedirect: !isTabletPortrait,
        withLoader,
      })
    },
    [auth, goToWorkspace, isTabletPortrait, onboardingDocPath, redirectTo, registrationType, workspace],
  )

  useEffect(() => {
    if (workspace && !workspace.seeded && workspaceSeeded && waitingForSeeds) redirectToWorkspace()
  }, [goToWorkspace, workspace, workspaceSeeded, waitingForSeeds, auth, redirectToWorkspace])

  const handleSubmit = async ({ selectedLanguage, ...registrationData }: Values) => {
    await updateRegistrationData({
      ...registrationData,
      ...utmParams,
      isMobile,
    })

    transitionPage('BLAZE_START_FREE_TRIAL')
  }

  return (
    <div data-testid="companyData" className={cx(Shared.content, Shared.isBlaze)}>
      <div className={Shared.leftPanel}>
        <div className={Shared.header}>
          <ProgressBar totalSteps={totalSteps} currentStep={3} />
        </div>
        <div>
          <div className={Styles.content}>
            <h1 className={Styles.heading}>We'd love to get more acquainted!</h1>
            <Formik
              enableReinitialize
              onSubmit={handleSubmit}
              validationSchema={validationSchema}
              initialValues={{
                businessType: data?.user_registration_data?.business_type || '',
                signupReason: data?.user_registration_data?.signup_reason || '',
                discoverySource: data?.user_registration_data?.discovery_source || '',
                teamSize: data?.user_registration_data?.team_size || '',
                selectedLanguage: DEFAULT_LANGUAGE,
              }}
            >
              {({ values, setFieldValue, touched, errors, submitForm }) => (
                <Form className={Styles.form}>
                  <CompanyDataSelect
                    isRequired
                    isInvalid={!!touched['businessType'] && !!errors['businessType']}
                    label="What best describes your business?"
                    size="lg"
                    aria-labelledby="business-type"
                    onSelect={(businessType) => setFieldValue('businessType', businessType)}
                    options={businessDescriptionOptions}
                    root={root}
                    backdropClass={Styles.backdrop}
                    popoverClass={Styles.popover}
                    selectedKey={values.businessType}
                  />
                  <CompanyDataSelect
                    isRequired
                    isInvalid={!!touched['signupReason'] && !!errors['signupReason']}
                    label="Why did you primarily sign up for Blaze?"
                    size="lg"
                    aria-labelledby="signup-reason"
                    onSelect={(signupReason) => setFieldValue('signupReason', signupReason)}
                    options={signUpOptions}
                    root={root}
                    backdropClass={Styles.backdrop}
                    popoverClass={Styles.popover}
                    selectedKey={values.signupReason}
                  />
                  <CompanyDataSelect
                    isRequired
                    isInvalid={!!touched['teamSize'] && !!errors['teamSize']}
                    label=" How big is your company?"
                    size="lg"
                    aria-labelledby="team-size"
                    onSelect={(teamSize) => setFieldValue('teamSize', teamSize)}
                    options={sizeOptions}
                    root={root}
                    backdropClass={Styles.backdrop}
                    popoverClass={Styles.popover}
                    selectedKey={values.teamSize}
                  />
                  <CompanyDataSelect
                    isRequired
                    isInvalid={!!touched['discoverySource'] && !!errors['discoverySource']}
                    label="How did you hear about Blaze?"
                    size="lg"
                    onSelect={(discoverySource) => setFieldValue('discoverySource', discoverySource)}
                    options={blazeDiscoveryOptions}
                    root={root}
                    backdropClass={Styles.backdrop}
                    popoverClass={Styles.popover}
                    selectedKey={values.discoverySource}
                  />
                  <div className={Styles.field}>
                    <div className={Styles.buttonGroup}>
                      <ResponsiveDesign.Desktop>
                        <Button variant="primary" size="lg" type="submit" endIcon={ArrowRight}>
                          Let's get going
                        </Button>
                      </ResponsiveDesign.Desktop>
                      <ResponsiveDesign.Mobile>
                        <Portal container={footerRef}>
                          <Button
                            variant="primary"
                            size="lg"
                            type="submit"
                            onPress={submitForm}
                            endIcon={ArrowRight}
                            fullWidth
                          >
                            Let's get going
                          </Button>
                        </Portal>
                      </ResponsiveDesign.Mobile>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
          <ResponsiveDesign.Mobile>
            <div className={Styles.footer} ref={footerRef} />
          </ResponsiveDesign.Mobile>
        </div>
      </div>
    </div>
  )
}

export default CompanyData
